/**
 * Outline-coverage check (PLA2-822).
 *
 * The AreaSelector exports its mask as a PNG data URL: filled polygons on a
 * transparent background. Coverage = share of pixels that are painted. Tiny
 * outlines (a thin strip along a foundation, a ring around a trunk) leave the
 * model almost no room to place plants, so the design step gates on this.
 */

/** Below this, Continue is blocked. */
export const MIN_MASK_COVERAGE = 0.05;
/** Below this (but above the minimum), show a soft "small area" note. */
export const SMALL_MASK_COVERAGE = 0.1;

// Sampling at ~256px on the long side is plenty for a percentage and keeps
// the pixel loop cheap on phones.
const SAMPLE_MAX_DIM = 256;

export function computeMaskCoverage(maskDataUrl: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(
        1,
        SAMPLE_MAX_DIM / Math.max(img.width, img.height)
      );
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas 2D context unavailable"));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      let painted = 0;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] > 127) painted++;
      }
      resolve(painted / (w * h));
    };
    img.onerror = () => reject(new Error("Failed to load mask image"));
    img.src = maskDataUrl;
  });
}

// ─── "Fix it for me" (PLA2-822) ─────────────────────────────────────────────

/** "Fix it for me" grows the outline until it covers about this share. */
export const FIX_MASK_TARGET_COVERAGE = 0.14;

type NormPoint = { x: number; y: number };
type NormPolygon = { id: string; points: NormPoint[] };

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Union coverage of normalized (0–1) polygons, rasterized on a square grid.
 * In normalized space the area fraction is independent of the photo's aspect.
 */
export function polygonCoverage(polygons: NormPolygon[]): number {
  const n = SAMPLE_MAX_DIM;
  const canvas = document.createElement("canvas");
  canvas.width = n;
  canvas.height = n;
  const ctx = canvas.getContext("2d");
  if (!ctx) return 0;
  ctx.fillStyle = "#000";
  for (const p of polygons) {
    if (p.points.length < 3) continue;
    ctx.beginPath();
    p.points.forEach((pt, i) =>
      i === 0 ? ctx.moveTo(pt.x * n, pt.y * n) : ctx.lineTo(pt.x * n, pt.y * n)
    );
    ctx.closePath();
    ctx.fill();
  }
  const { data } = ctx.getImageData(0, 0, n, n);
  let painted = 0;
  for (let i = 3; i < data.length; i += 4) if (data[i] > 127) painted++;
  return painted / (n * n);
}

/**
 * Grow each polygon so the outline reaches full bed depth. Thin outlines are
 * almost always a strip traced along a wall, driveway or trunk, so growth is
 * mostly downward (toward the camera) from near the polygon's top edge, with
 * a smaller widening. Every polygon uses the same scale; the smallest scale
 * that reaches the target coverage wins (binary search). Points are clamped
 * to the photo, and the result stays editable in the AreaSelector.
 */
export function growPolygonsToCoverage(
  polygons: NormPolygon[],
  target = FIX_MASK_TARGET_COVERAGE
): NormPolygon[] {
  const valid = polygons.filter((p) => p.points.length >= 3);
  if (valid.length === 0) return polygons;

  const grow = (s: number): NormPolygon[] =>
    valid.map((p) => {
      const xs = p.points.map((pt) => pt.x);
      const ys = p.points.map((pt) => pt.y);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
      // Anchor a quarter of the way down: grows ~3x more toward the camera
      // than upward into the wall/house.
      const anchorY = minY + (maxY - minY) * 0.25;
      const sx = 1 + (s - 1) * 0.1;
      return {
        id: p.id,
        points: p.points.map((pt) => ({
          x: clamp01(cx + (pt.x - cx) * sx),
          y: clamp01(anchorY + (pt.y - anchorY) * s),
        })),
      };
    });

  const MAX_SCALE = 12;
  if (polygonCoverage(grow(MAX_SCALE)) < target) return grow(MAX_SCALE);
  let lo = 1;
  let hi = MAX_SCALE;
  for (let i = 0; i < 14; i++) {
    const mid = (lo + hi) / 2;
    if (polygonCoverage(grow(mid)) >= target) hi = mid;
    else lo = mid;
  }
  return grow(hi);
}
